import type { FrontRoomsView } from '@obraplan/core/application/contracts/takeoff';
import { chaveDoComodo, nomeDoApto } from './planta-pintada';

/**
 * O que se digita no apontamento do dia por cômodo (Fase 34, etapa 2 — RN-443, RN-444).
 *
 * O rascunho guarda só o que a pessoa mexeu: o texto do percentual de cada apto × cômodo e a
 * marca do "não se aplica" de cada cômodo da tipologia. O que não está nele vale o que o servidor
 * leu. Ao salvar o dia, `mudancasDoRascunho` diz o que gravar — e o que está errado, antes de
 * gravar qualquer coisa.
 */
export interface RascunhoDosComodos {
  /** Por `chaveDoComodo`: o texto digitado, "72,5" ou "". */
  readonly percentuais: Readonly<Record<string, string>>;
  /** Por cômodo da tipologia (`roomId`): vale para todos os aptos dela (RN-444). */
  readonly naoSeAplica: Readonly<Record<string, boolean>>;
}

export const RASCUNHO_VAZIO: RascunhoDosComodos = { percentuais: {}, naoSeAplica: {} };

export function rascunhoVazio(rascunho: RascunhoDosComodos): boolean {
  return (
    Object.keys(rascunho.percentuais).length === 0 && Object.keys(rascunho.naoSeAplica).length === 0
  );
}

/** "72,5" → 72.5; "" → undefined; "abc", "-1", "120" → `null` (recusado). */
export function lerPercentual(texto: string): number | undefined | null {
  const limpo = texto.trim().replace(',', '.');
  if (limpo === '') return undefined;
  const valor = Number(limpo);
  if (!Number.isFinite(valor) || valor < 0 || valor > 100) return null;
  return valor;
}

export interface MudancasDosComodos {
  /** O percentual do dia dos cômodos que mudaram (`RecordRoomProgress`). */
  readonly rooms: readonly {
    readonly positionId: string;
    readonly roomId: string;
    readonly percent: number;
  }[];
  /** O "não se aplica" que mudou (`SetRoomNotApplicable`), um por cômodo da tipologia. */
  readonly notApplicable: readonly { readonly roomId: string; readonly notApplicable: boolean }[];
}

export type ResultadoDoRascunho =
  | { readonly ok: true; readonly value: MudancasDosComodos }
  | { readonly ok: false; readonly message: string };

/**
 * O que o rascunho muda no que o servidor leu. O percentual de um cômodo que fica "não se aplica"
 * não vai (o servidor recusaria); o campo apagado não apaga o que valia — a trilha só acrescenta,
 * e "0" é o que volta o cômodo a não começado.
 */
export function mudancasDoRascunho(
  view: FrontRoomsView,
  rascunho: RascunhoDosComodos,
): ResultadoDoRascunho {
  const notApplicable: { roomId: string; notApplicable: boolean }[] = [];
  const vistos = new Set<string>();
  for (const room of view.rooms) {
    if (vistos.has(room.roomId)) continue;
    vistos.add(room.roomId);
    const marcado = rascunho.naoSeAplica[room.roomId];
    if (marcado !== undefined && marcado !== room.notApplicable) {
      notApplicable.push({ roomId: room.roomId, notApplicable: marcado });
    }
  }

  const rooms: { positionId: string; roomId: string; percent: number }[] = [];
  for (const room of view.rooms) {
    const texto = rascunho.percentuais[chaveDoComodo(room)];
    if (texto === undefined) continue;
    if (rascunho.naoSeAplica[room.roomId] ?? room.notApplicable) continue;
    const valor = lerPercentual(texto);
    if (valor === null) {
      return {
        ok: false,
        message: `O percentual de ${room.roomName} · ${nomeDoApto(room.positionLabel)} vai de 0 a 100.`,
      };
    }
    if (valor === undefined || valor === room.percent) continue;
    rooms.push({ positionId: room.positionId, roomId: room.roomId, percent: valor });
  }
  return { ok: true, value: { rooms, notApplicable } };
}
